import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, RefreshCw, Download, ShieldCheck, Search,
  CheckCircle2, AlertTriangle, Clock, Send, ReceiptText, FileText,
  Link2, Unlink, FileSpreadsheet, RotateCw,
} from "lucide-react";

const STATUS_META = {
  RECEIVED: { label: "Received (queued)", cls: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300", icon: Clock },
  SENT: { label: "Sent to FTA", cls: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300", icon: Send },
  DELIVERED: { label: "Delivered", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300", icon: CheckCircle2 },
  REPORTED: { label: "Reported (FTA)", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300", icon: CheckCircle2 },
  APPROVED: { label: "Approved", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300", icon: CheckCircle2 },
  NOT_COMPLIANT: { label: "Not compliant", cls: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300", icon: AlertTriangle },
  FAILED: { label: "Failed", cls: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300", icon: AlertTriangle },
  REJECTED: { label: "Rejected", cls: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300", icon: AlertTriangle },
  INVALID: { label: "Invalid", cls: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300", icon: AlertTriangle },
  ACKNOWLEDGED: { label: "Acknowledged", cls: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300", icon: CheckCircle2 },
  ERROR: { label: "Submission error", cls: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300", icon: AlertTriangle },
};
const IN_PROGRESS = ["RECEIVED", "ACKNOWLEDGED", "SENT"];
const CLEARED = ["APPROVED", "REPORTED", "DELIVERED"];
const FAILED_SET = ["REJECTED", "INVALID", "NOT_COMPLIANT", "FAILED"];
const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
};

export default function JamClearance() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [plan, setPlan] = useState(null);
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusF, setStatusF] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const [q, setQ] = useState("");
  const [dl, setDl] = useState("");
  const [retrying, setRetrying] = useState("");

  const load = useCallback((isRefresh) => {
    if (isRefresh) setRefreshing(true);
    Promise.all([
      api.get("/einvoice/submissions"),
      api.get("/einvoice/subscription"),
      api.get("/einvoice/wafeq/status"),
    ]).then(([s, sub, w]) => {
      setSubs(s.data.submissions || []);
      setPlan(sub.data);
      setConn(w.data);
    }).catch(() => {}).finally(() => { setLoading(false); setRefreshing(false); });
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh while any LIVE document is still in progress — webhook-driven
  // status changes (Received → Reported/Cleared) then surface without a manual refresh.
  const hasPending = useMemo(
    () => subs.some((x) => x.mode === "live" && IN_PROGRESS.includes(x.status)),
    [subs]);
  useEffect(() => {
    if (!hasPending) return;
    const t = setInterval(() => load(), 15000);
    return () => clearInterval(t);
  }, [hasPending, load]);

  const counts = useMemo(() => {
    const c = { total: subs.length, RECEIVED: 0, APPROVED: 0, REJECTED_INVALID: 0, ERROR: 0 };
    subs.forEach((x) => {
      if (IN_PROGRESS.includes(x.status) || x.status === "DELIVERED") c.RECEIVED += 1;
      else if (CLEARED.includes(x.status)) c.APPROVED += 1;
      else if (FAILED_SET.includes(x.status)) c.REJECTED_INVALID += 1;
      else if (x.status === "ERROR") c.ERROR += 1;
    });
    return c;
  }, [subs]);

  const filtered = useMemo(() => {
    return subs.filter((x) => {
      if (typeF !== "all" && x.doc_kind !== typeF) return false;
      if (statusF === "received" && !(IN_PROGRESS.includes(x.status) || x.status === "DELIVERED")) return false;
      if (statusF === "approved" && !CLEARED.includes(x.status)) return false;
      if (statusF === "rejected" && !FAILED_SET.includes(x.status)) return false;
      if (statusF === "error" && x.status !== "ERROR") return false;
      if (q && !(x.invoice_number || "").toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [subs, typeF, statusF, q]);

  const basePath = (x) => x.doc_kind === "ipc" ? `/einvoice/ipcs/${x.doc_id}`
    : x.doc_kind === "credit_note" ? `/einvoice/credit-notes/${x.doc_id}`
      : `/einvoice/standard-invoices/${x.doc_id}`;

  const download = async (x) => {
    setDl(x.id);
    try {
      await downloadFile(`${basePath(x)}/download?fmt=pdf`, `${x.invoice_number || "invoice"}.pdf`);
    } catch { toast.error("Could not download the document"); }
    finally { setDl(""); }
  };

  const resubmit = async (x) => {
    setRetrying(x.id);
    try {
      await api.post(`${basePath(x)}/submit-wafeq`);
      toast.success(`${x.invoice_number || "Document"} resubmitted to the FTA via Wafeq`);
      load(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resubmission failed — check the document data and try again");
    } finally { setRetrying(""); }
  };

  const exportCsv = () => {
    const rows = filtered.length ? filtered : subs;
    const bucket = (s) => IN_PROGRESS.includes(s) || s === "DELIVERED" ? "Pending"
      : CLEARED.includes(s) ? "Cleared"
        : FAILED_SET.includes(s) ? "Failed"
          : s === "ERROR" ? "Error" : "Other";
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Invoice #", "Type", "Status", "Category", "Mode", "ASP document ID", "Submitted", "Last update"];
    const lines = rows.map((x) => [
      x.invoice_number || "", typeLabel(x.doc_kind),
      (STATUS_META[x.status]?.label) || x.status_label || x.status, bucket(x.status),
      x.mode_label || (x.mode === "live" ? "Live · Approved ASP" : "Simulation"),
      x.jam_uuid || x.wafeq_document_id || "", fmtDate(x.created_at), fmtDate(x.updated_at),
    ].map(esc).join(","));
    const csv = [header.map(esc).join(","), ...lines].join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fta-clearance-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} submission${rows.length === 1 ? "" : "s"} to CSV`);
  };

  const typeLabel = (k) => k === "ipc" ? "Construction IPC" : k === "credit_note" ? "Credit note" : "Standard";

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  const isFull = plan?.plan === "full_service" || plan?.free;

  return (
    <div data-testid="jam-clearance-page">
      <button onClick={() => navigate("/einvoicing/app")} data-testid="clearance-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" /> Matiqs Qonnect</button>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-brand" /> ASP clearance</h1>
          <p className="text-sm text-muted-foreground mt-1">Every invoice submitted to the FTA via our Approved ASP — standard and construction — with its clearance status.</p>
          {hasPending && (
            <span data-testid="clearance-autorefresh" className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-medium text-amber-600 dark:text-amber-400">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span></span>
              Live — auto-checking for FTA clearance updates every 15s
            </span>
          )}
        </div>
        <div className="flex items-center gap-2.5">
          <ConnBadge conn={conn} />
          <Button data-testid="clearance-export-csv" variant="outline" onClick={exportCsv} disabled={subs.length === 0} className="gap-1.5">
            <FileSpreadsheet className="w-4 h-4" /> Export CSV
          </Button>
          <Button data-testid="clearance-refresh" variant="outline" onClick={() => load(true)} disabled={refreshing} className="gap-1.5">
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </Button>
        </div>
      </div>

      {!isFull && (
        <div data-testid="clearance-upsell" className="rounded-2xl border border-indigo-300 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/20 p-5 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 text-sm text-slate-700 dark:text-slate-300">
            <Send className="w-5 h-5 text-indigo-600 dark:text-indigo-400 shrink-0" />
            <span><strong>Full Service</strong> lets Matiqs Qonnect submit your invoices directly to the FTA via our Approved ASP and track clearance here. Switch plan to enable it.</span>
          </div>
          <Button data-testid="clearance-upsell-btn" onClick={() => navigate("/einvoicing/app")} className="gap-1.5">Choose a plan</Button>
        </div>
      )}

      {/* Summary cards (clickable filters) */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="clearance-summary">
        <SummaryCard testid="sum-total" active={statusF === "all"} onClick={() => setStatusF("all")} label="Total" value={counts.total} tone="muted" />
        <SummaryCard testid="sum-received" active={statusF === "received"} onClick={() => setStatusF("received")} label="In progress" value={counts.RECEIVED} tone="amber" icon={Clock} />
        <SummaryCard testid="sum-approved" active={statusF === "approved"} onClick={() => setStatusF("approved")} label="Reported / cleared" value={counts.APPROVED} tone="emerald" icon={CheckCircle2} />
        <SummaryCard testid="sum-rejected" active={statusF === "rejected"} onClick={() => setStatusF("rejected")} label="Not compliant / failed" value={counts.REJECTED_INVALID} tone="red" icon={AlertTriangle} />
        <SummaryCard testid="sum-error" active={statusF === "error"} onClick={() => setStatusF("error")} label="Errors" value={counts.ERROR} tone="red" icon={AlertTriangle} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="clearance-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice number" className="pl-9" />
        </div>
        <Select value={typeF} onValueChange={setTypeF}>
          <SelectTrigger data-testid="clearance-type-filter" className="w-[190px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All document types</SelectItem>
            <SelectItem value="standard">Standard invoices</SelectItem>
            <SelectItem value="ipc">Construction IPCs</SelectItem>
            <SelectItem value="credit_note">Credit notes</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="clearance-status-filter" className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="received">In progress</SelectItem>
            <SelectItem value="approved">Reported / cleared</SelectItem>
            <SelectItem value="rejected">Not compliant / failed</SelectItem>
            <SelectItem value="error">Errors</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div data-testid="clearance-empty" className="rounded-2xl border border-dashed border-border bg-card py-16 text-center">
          <ShieldCheck className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground">{subs.length === 0 ? "Nothing submitted to the FTA yet. Submit an invoice or IPC from its page." : "No submissions match these filters."}</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left font-bold">Invoice #</th>
                <th className="px-4 py-2.5 text-left font-bold">Type</th>
                <th className="px-4 py-2.5 text-left font-bold">Status</th>
                <th className="px-4 py-2.5 text-left font-bold">Submitted</th>
                <th className="px-4 py-2.5 text-left font-bold">Last update</th>
                <th className="px-4 py-2.5 text-right font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((x) => {
                const meta = STATUS_META[x.status] || { label: x.status_label || x.status, cls: "bg-accent text-muted-foreground", icon: Clock };
                const Icon = meta.icon;
                return (
                  <tr key={x.id} className="border-t border-border" data-testid={`clearance-row-${x.invoice_number}`}>
                    <td className="px-4 py-3 font-semibold text-foreground">{x.invoice_number || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                        {x.doc_kind === "ipc" ? <FileText className="w-3.5 h-3.5" /> : x.doc_kind === "credit_note" ? <ReceiptText className="w-3.5 h-3.5 text-rose-500" /> : <ReceiptText className="w-3.5 h-3.5" />}
                        {typeLabel(x.doc_kind)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span data-testid={`clearance-status-${x.invoice_number}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.cls}`}>
                          <Icon className={`w-3 h-3 ${x.status === "RECEIVED" ? "animate-pulse" : ""}`} /> {meta.label}
                        </span>
                        <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${x.mode === "live" ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300" : "bg-accent text-muted-foreground"}`}>
                          {x.mode_label || (x.mode === "live" ? "Live · Approved ASP" : "Simulation")}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{fmtDate(x.created_at)}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{fmtDate(x.updated_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {(x.status === "ERROR" || FAILED_SET.includes(x.status)) && (
                          <Button data-testid={`clearance-resubmit-${x.invoice_number}`} variant="ghost" size="sm" className="gap-1.5 h-7 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700" disabled={retrying === x.id} onClick={() => resubmit(x)} title="Resubmit this document to the FTA via our Approved ASP">
                            {retrying === x.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />} Resubmit
                          </Button>
                        )}
                        <Button data-testid={`clearance-download-${x.invoice_number}`} variant="ghost" size="sm" className="gap-1.5 h-7 text-xs" disabled={dl === x.id} onClick={() => download(x)}>
                          {dl === x.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-start gap-2 text-xs text-muted-foreground mt-6">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-emerald-500" />
        <span>Statuses marked <strong>Simulation</strong> are generated locally for testing and are not real FTA clearance. <strong>Live · Approved ASP</strong> submissions are sent for real to the FTA through our Approved ASP once this business is connected; clearance updates arrive on the ASP's signed webhook.</span>
      </div>
    </div>
  );
}

const TONES = {
  muted: "border-border",
  amber: "border-amber-300/60 dark:border-amber-900/60",
  emerald: "border-emerald-300/60 dark:border-emerald-900/60",
  red: "border-red-300/60 dark:border-red-900/60",
};
const ConnBadge = ({ conn }) => {
  if (!conn) return null;
  const active = !!conn.connected;
  const Icon = active ? Link2 : Unlink;
  const cls = active
    ? "bg-emerald-100 text-emerald-700 border-emerald-300/60 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60"
    : "bg-amber-100 text-amber-700 border-amber-300/60 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/60";
  return (
    <span data-testid="fta-connection-badge"
      title={active ? `Connected via our Approved ASP${conn.taxpayer_id ? ` · taxpayer ${conn.taxpayer_id}` : ""}${conn.connected_at ? ` · since ${fmtDate(conn.connected_at)}` : ""}` : "No FTA connection — connect this business to submit invoices"}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[11px] font-semibold ${cls}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span>FTA connection: {active ? "Active" : "Not linked"}</span>
      {active && conn.tin && <span className="font-mono font-bold">· TIN {conn.tin}</span>}
    </span>
  );
};

const SummaryCard = ({ label, value, tone, icon: Icon, active, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`text-left rounded-xl border bg-card p-4 transition-all hover:shadow-sm ${TONES[tone] || "border-border"} ${active ? "ring-2 ring-brand/40" : ""}`}>
    <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {Icon && <Icon className="w-3.5 h-3.5" />} {label}
    </div>
    <div className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{value}</div>
  </button>
);
